import assistantHebrew from '@fontsource-variable/assistant/files/assistant-hebrew-wght-normal.woff2?url';
import assistantLatinExt from '@fontsource-variable/assistant/files/assistant-latin-ext-wght-normal.woff2?url';
import assistantLatin from '@fontsource-variable/assistant/files/assistant-latin-wght-normal.woff2?url';
import { clientEnv } from '@pricedrop/config/client';
import { m } from '@pricedrop/i18n';
import * as Sentry from '@sentry/tanstackstart-react';
import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  Scripts,
  useRouterState,
} from '@tanstack/react-router';
import { ThemeProvider } from 'next-themes';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import { useEffect } from 'react';
import { GoogleOneTap } from '@/components/auth/google-one-tap';
import { Footer } from '@/components/layout/footer';
import { Header } from '@/components/layout/header';
import { Button } from '@/components/ui/button';
import { DirectionProvider } from '@/components/ui/direction';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthModalProvider } from '@/lib/auth-modal-context';
import { I18nProvider, useI18n } from '@/lib/i18n';
import { useUmamiPageviewTracking } from '@/lib/umami-tracker';
import appCss from '../styles.css?url';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      {
        title:
          'Pricedrop - \u05DE\u05E2\u05E7\u05D1 \u05D4\u05D9\u05E1\u05D8\u05D5\u05E8\u05D9\u05D9\u05EA \u05DE\u05D7\u05D9\u05E8\u05D9\u05DD \u05D1-KSP',
      },
      { name: 'theme-color', content: '#155dfc' },
    ],
    links: [
      { rel: 'preconnect', href: 'https://images.pricedrop.co.il' },
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: assistantHebrew,
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: assistantLatin,
        crossOrigin: 'anonymous',
      },
      {
        rel: 'preload',
        as: 'font',
        type: 'font/woff2',
        href: assistantLatinExt,
        crossOrigin: 'anonymous',
      },
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
      { rel: 'icon', href: '/favicon.ico', sizes: '32x32' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      { rel: 'manifest', href: '/manifest.json' },
    ],
  }),
  shellComponent: RootShell,
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: ErrorFallback,
});

function NotFound() {
  return (
    <div className="my-24 flex w-full flex-col items-center space-y-6 px-4">
      <h1 className="text-3xl font-bold sm:text-4xl md:text-5xl lg:text-6xl">
        {m.notFound_error()} <span className="text-destructive">{m.notFound_code()}</span>
      </h1>
      <h2 className="text-center text-xl sm:text-2xl md:text-3xl">{m.notFound_title()}</h2>
      <p className="text-center text-muted-foreground">{m.notFound_description()}</p>
      <Button asChild>
        <Link to="/">{m.common_backToHome()}</Link>
      </Button>
    </div>
  );
}

function ErrorFallback({ error }: { error: Error }) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const statusCode =
    (error as { statusCode?: number; status?: number })?.statusCode ||
    (error as { statusCode?: number; status?: number })?.status ||
    500;

  return (
    <div className="my-24 flex w-full flex-col items-center space-y-6 px-4">
      <h1 className="text-3xl font-bold sm:text-4xl md:text-5xl lg:text-6xl">
        {m.error_title()} <span className="text-destructive">{statusCode}</span>
      </h1>
      <h2 className="text-center text-xl sm:text-2xl md:text-3xl">{m.error_generic()}</h2>

      {import.meta.env.DEV && (
        <div className="mt-4 max-w-2xl rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="mb-2 text-sm font-semibold">{m.error_devInfo()}</p>
          <pre className="overflow-auto text-xs text-muted-foreground">
            {error.stack || error.toString()}
          </pre>
        </div>
      )}

      <div className="flex gap-4">
        <Button asChild>
          <Link to="/">{m.common_backToHome()}</Link>
        </Button>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {m.error_retry()}
        </Button>
      </div>
    </div>
  );
}

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="he" dir="rtl" suppressHydrationWarning>
      <head>
        <HeadContent />
        {clientEnv.UMAMI_URL && clientEnv.UMAMI_WEBSITE_ID && (
          <script
            defer
            src={`${clientEnv.UMAMI_URL}/script.js`}
            data-website-id={clientEnv.UMAMI_WEBSITE_ID}
            data-auto-track="false"
          />
        )}
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootDocument() {
  const { locale, direction } = useI18n();
  const isEmbed = useRouterState({ select: (s) => s.location.pathname.startsWith('/embed') });

  useUmamiPageviewTracking();

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
  }, [locale, direction]);

  if (isEmbed) {
    return (
      <DirectionProvider dir={direction}>
        <Outlet />
        <Toaster />
      </DirectionProvider>
    );
  }

  return (
    <NuqsAdapter>
      <DirectionProvider dir={direction}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <TooltipProvider>
            <AuthModalProvider>
              <GoogleOneTap />
              <div className="flex min-h-screen flex-col">
                <Header />
                <main className="mx-auto my-2 w-full max-w-screen-2xl flex-1 px-4 md:my-4">
                  <Outlet />
                </main>
                <Footer />
              </div>
              <Toaster />
            </AuthModalProvider>
          </TooltipProvider>
        </ThemeProvider>
      </DirectionProvider>
    </NuqsAdapter>
  );
}

function RootLayout() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <RootDocument />
      </I18nProvider>
    </QueryClientProvider>
  );
}
